import { useState, useEffect, useRef } from "react";
import { Bell, User, Menu, X, LogOut, ChevronDown, PanelRightOpen } from "lucide-react";
import Images from "../../utils/Images";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../store/hooks";
import { clearAuth } from "../../store/slices/authSlice";

export default function AuthNavbar({ onMobileMenuToggle, user = null }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [notifications,] = useState(1);
  const navigate = useNavigate();
  const profileDropdownRef = useRef(null);
  const dispatch = useAppDispatch();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {  
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  const handleNotificationClick = () => {
    navigate("/notifications");
  };

  const handleProfileClick = () => {
    navigate("/profile");
  };

  const handleSignOut = () => {
    dispatch(clearAuth());
    navigate("/");
  };

  const handleMobileSidebarToggle = () => {
    if (onMobileMenuToggle) {
      onMobileMenuToggle();
    }
  };

  return (
    <nav className="bg-black border-gray-800 brder-b">
      {/* Top blue accent strip */}
      <div className="h-1 bg-blue-400"></div>
      
      <div className="px-4 mx-auto max-w-9xl sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left side - Mobile Menu Button and Logo */}
          <div className="flex items-center space-x-3">
            {/* Mobile Menu Button */}
            <button
              onClick={handleMobileSidebarToggle}
              className="p-2 text-gray-300 transition-colors duration-200 hover:text-white lg:hidden"
            >
              <PanelRightOpen size={24} />
            </button>
            
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center space-x-2">
              <img 
                src={Images.logo_colored_white} 
                alt="TripSecure" 
                className="w-auto h-8"
              />
            
            </Link>
          </div>

          {/* Right side - Navigation items */}
          <div className="hidden items-center space-x-6 md:flex">
            {/* Notification Bell */}
            <button
              onClick={handleNotificationClick}
              className="relative p-2 text-gray-300 transition-colors duration-200 hover:text-white"
            >
              <Bell size={20} />
              {notifications > 0 && (
                <span className="flex absolute -top-1 -right-1 justify-center items-center w-5 h-5 text-xs text-white bg-red-500 rounded-full">
                  {notifications}
                </span>
              )}
            </button>

            {/* Profile Icon with Dropdown */}
            <div className="relative" ref={profileDropdownRef}>
              <button
                onClick={toggleProfileDropdown}
                className="flex items-center p-2 space-x-2 text-emerald-400 transition-colors duration-200 hover:text-emerald-300"
              >
                <User size={20} />
                <ChevronDown size={16} className={`transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Profile Dropdown */}
              {isProfileDropdownOpen && (
                <div className="absolute right-0 z-50 py-1 mt-2 w-48 bg-white rounded-md shadow-lg">
                  <button
                    onClick={handleProfileClick}
                    className="flex items-center px-4 py-2 w-full text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <User size={16} className="mr-2" />
                    Profile
                  </button>
                
                  <hr className="my-1" />
                  <button
                    onClick={handleSignOut}
                    className="flex items-center px-4 py-2 w-full text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut size={16} className="mr-2" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button - Positioned absolutely on the right */}
          <div className="flex items-center md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="p-2 text-gray-300 transition-colors duration-200 hover:text-white"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="bg-gray-800 border-t border-gray-700 md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link
              to="/dashboard"
              className="block px-3 py-2 text-gray-300 rounded-md transition-colors duration-200 hover:text-white hover:bg-gray-700"
            >
              Dashboard
            </Link>
            <Link
              to="/policies"
              className="block px-3 py-2 text-gray-300 rounded-md transition-colors duration-200 hover:text-white hover:bg-gray-700"
            >
              Policies
            </Link>
            <Link
              to="/claims"
              className="block px-3 py-2 text-gray-300 rounded-md transition-colors duration-200 hover:text-white hover:bg-gray-700"
            >
              Claims
            </Link>
            <Link
              to="/profile"
              className="block px-3 py-2 text-gray-300 rounded-md transition-colors duration-200 hover:text-white hover:bg-gray-700"
            >
              Profile
            </Link>
            <Link
              to="/notifications"
              className="block px-3 py-2 text-gray-300 rounded-md transition-colors duration-200 hover:text-white hover:bg-gray-700"
            >
              Notifications
              {notifications > 0 && (
                <span className="px-2 py-1 ml-2 text-xs text-white bg-red-500 rounded-full">
                  {notifications}
                </span>
              )}
            </Link>
            <hr className="border-gray-600" />
            <button
              onClick={handleSignOut}
              className="flex items-center px-3 py-2 w-full text-red-400 rounded-md transition-colors duration-200 hover:text-red-300 hover:bg-gray-700"
            >
              <LogOut size={16} className="mr-2" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
